import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TextField, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material';

const API_URL = (process.env.REACT_APP_API_URL || 'https://swadvedamasalasbe.onrender.com/api').trim();

const AdminInventory = () => {
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [movementOpen, setMovementOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const token = localStorage.getItem('token');

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/inventory`, { headers: { Authorization: `Bearer ${token}` } });
      setRows((res.data?.inventory || []).map((r) => ({ ...r, draftStock: r.stock, draftReorder: r.reorderLevel, draftSku: r.sku || '', note: '' })));
    } catch {
      setMessage('Failed to fetch inventory');
    }
  };

  useEffect(() => { fetchInventory(); }, []);
  const filteredRows = useMemo(() => rows.filter((r) => `${r.productName} ${r.sku} ${r.category}`.toLowerCase().includes(search.toLowerCase())), [rows, search]);

  const updateRow = async (row) => {
    try {
      await axios.put(`${API_URL}/admin/inventory/${row.productId}`, {
        sku: row.draftSku,
        stock: Number(row.draftStock),
        reorderLevel: Number(row.draftReorder),
        note: row.note
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Inventory updated successfully');
      fetchInventory();
      setDetailOpen(false);
    } catch (e) {
      setMessage(e.response?.data?.message || 'Inventory update failed');
    }
  };

  const openMovements = async (row) => {
    const res = await axios.get(`${API_URL}/admin/inventory/movements/${row.productId}`, { headers: { Authorization: `Bearer ${token}` } });
    setMovements(res.data?.movements || []);
    setMovementOpen(true);
  };

  const openDetails = (row) => { setSelected({ ...row }); setDetailOpen(true); };

  const downloadReport = async () => {
    const response = await axios.get(`${API_URL}/admin/inventory/report/download`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ color: '#8B0000', fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>Masala Inventory Management</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={downloadReport}>Download Report</Button>
          <Button variant="contained" onClick={() => window.print()}>Print</Button>
        </Box>
      </Box>
      <TextField fullWidth size="small" placeholder="Search by product/SKU/category" sx={{ mb: 2 }} value={search} onChange={(e) => setSearch(e.target.value)} />
      {message && <Alert sx={{ mb: 2 }} severity="info" onClose={() => setMessage('')}>{message}</Alert>}

      <Paper sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell><TableCell>Product</TableCell><TableCell>In Stock</TableCell><TableCell>In Orders</TableCell><TableCell>Available</TableCell><TableCell>Low Stock</TableCell><TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow key={String(row.productId)} sx={{ backgroundColor: row.lowStock ? '#fff4f4' : 'inherit' }}>
                <TableCell>{row.sku}</TableCell>
                <TableCell>{row.productName}</TableCell>
                <TableCell>{row.stock}</TableCell>
                <TableCell>{row.inOrder}</TableCell>
                <TableCell>{row.availableToSell}</TableCell>
                <TableCell>{row.lowStock ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={() => openDetails(row)}>Details</Button>
                    <Button variant="outlined" onClick={() => openMovements(row)}>History</Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Product Inventory Details</DialogTitle>
        <DialogContent>
          {selected && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Product" value={selected.productName} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Category" value={selected.category || ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Price" value={`₹${Number(selected.price || 0).toFixed(2)}`} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="In Orders" value={selected.inOrder} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Available To Sell" value={selected.availableToSell} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="SKU" value={selected.draftSku ?? selected.sku ?? ''} onChange={(e) => setSelected({ ...selected, draftSku: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Reorder Level" value={selected.draftReorder ?? selected.reorderLevel ?? 0} onChange={(e) => setSelected({ ...selected, draftReorder: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Current Stock" value={selected.draftStock ?? selected.stock ?? 0} onChange={(e) => setSelected({ ...selected, draftStock: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Adjustment Note" value={selected.note || ''} onChange={(e) => setSelected({ ...selected, note: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => updateRow(selected)}>Update Inventory</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={movementOpen} onClose={() => setMovementOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Inventory Movement History</DialogTitle>
        <DialogContent>
          {movements.map((m) => (
            <Box key={m._id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
              <Typography variant="body2">{new Date(m.createdAt).toLocaleString()} | {m.movementType} | {m.previousStock} → {m.newStock} (Δ {m.delta})</Typography>
              <Typography variant="caption" color="text.secondary">{m.note}</Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminInventory;
